import {useEffect, useRef, useState} from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import EmptyData from "@/components/EmptyData";
import VLFModal from "@/components/Modal";
import VLFUpload from "@/components/Input/Upload";

import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import AudioFileOutlinedIcon from "@mui/icons-material/AudioFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

import {AttachmentAPI} from "@/api/attachment";
import pathUrl from "@/utils/path";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";

import "./index.scss";

// verificare se il file è un video
const isVideoFile = (filePath) => {
	const extension = filePath.split(".").pop().toLowerCase();
	return ["mp4", "mov", "avi", "webm", "ogv"].includes(extension);
};

// verificare se il file è un'immagine
const isImageFile = (filePath) => {
	const extension = filePath.split(".").pop().toLowerCase();
	return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "avif"].includes(
		extension,
	);
};

const getAttachmentName = (attachment) =>
	attachment.name || attachment.filePath.split(/[\\/]/).pop() || "---";

const getAttachmentExtension = (name) => {
	if (!name || name.indexOf(".") === -1) {
		return "";
	}
	return name.split(".").pop().toLowerCase();
};

const previewFormats = {
	png: ["image", "image/png"],
	jpg: ["image", "image/jpeg"],
	jpeg: ["image", "image/jpeg"],
	gif: ["image", "image/gif"],
	webp: ["image", "image/webp"],
	bmp: ["image", "image/bmp"],
	avif: ["image", "image/avif"],
	pdf: ["pdf", "application/pdf"],
	mp4: ["video", "video/mp4"],
	webm: ["video", "video/webm"],
	ogv: ["video", "video/ogg"],
	mov: ["video", "video/quicktime"],
	mp3: ["audio", "audio/mpeg"],
	wav: ["audio", "audio/wav"],
	ogg: ["audio", "audio/ogg"],
	m4a: ["audio", "audio/mp4"],
	aac: ["audio", "audio/aac"],
	flac: ["audio", "audio/flac"],
	txt: ["text", "text/plain"],
	log: ["text", "text/plain"],
	csv: ["text", "text/plain"],
	json: ["text", "text/plain"],
	xml: ["text", "text/plain"],
};
const getPreviewFormat = (attachment) => {
	const format =
		previewFormats[
			getAttachmentExtension(
				attachment.filePath || getAttachmentName(attachment),
			)
		];
	if (!format) return null;
	if (
		["video", "audio"].includes(format[0]) &&
		!document.createElement(format[0]).canPlayType(format[1])
	)
		return null;
	if (format[0] === "pdf" && navigator.pdfViewerEnabled === false) return null;
	return format;
};

const getAttachmentTypeLabel = (attachment, name) => {
	if (isImageFile(attachment.filePath)) {
		return "Immagine";
	}
	if (isVideoFile(attachment.filePath)) {
		return "Video";
	}
	return getAttachmentExtension(name).toUpperCase() || "File";
};

export const getRelativePath = (absolutePath, apiUrl) => {
	const relativePath = absolutePath.split("\\uploads\\")[1];
	return relativePath
		? `${apiUrl}/uploads/${relativePath.replace(/\\/g, "/")}`
		: absolutePath;
};

function AuthenticatedAttachmentImage({attachment, className}) {
	const [preview, setPreview] = useState({});
	useEffect(() => {
		let active = true;
		let objectUrl;
		setPreview({});
		AttachmentAPI.getById(attachment.id)
			.then((blob) => {
				if (!active) return;
				const extension = getAttachmentExtension(attachment.filePath);
				const type = extension === "jpg" ? "jpeg" : extension;
				objectUrl = URL.createObjectURL(
					new Blob([blob], {type: `image/${type}`}),
				);
				setPreview({url: objectUrl});
			})
			.catch(() => {
				if (active) setPreview({error: true});
			});
		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [attachment.id, attachment.filePath]);
	return preview.url ? (
		<img
			className={className}
			src={preview.url}
			alt={getAttachmentName(attachment)}
		/>
	) : (
		<span>
			{preview.error ? "Anteprima non disponibile" : "Caricamento anteprima..."}
		</span>
	);
}

export default function AttachmentsContainer(props) {
	const {
		attachments,
		attachmentClassName,
		entityType,
		entityId,
		handleAdd,
		handleDownload,
		handleView,
		handleDelete,
		handleEdit,
		handleNotification,
		editable = true,
		authenticatedImages = true,
	} = props;

	const [loading, setLoading] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [preview, setPreview] = useState(null);
	const previewRequest = useRef(0);
	const [downloadingId, setDownloadingId] = useState(null);
	useEffect(() => {
		return () => {
			if (preview?.url) URL.revokeObjectURL(preview.url);
		};
	}, [preview?.url]);
	useEffect(
		() => () => {
			previewRequest.current += 1;
		},
		[],
	);
	const closePreview = () => {
		previewRequest.current += 1;
		setPreview(null);
	};
	const downloadAttachment = async (attachment) => {
		if (handleDownload) return handleDownload(attachment.id);
		setDownloadingId(attachment.id);
		try {
			const blob =
				preview?.attachment.id === attachment.id && preview.blob
					? preview.blob
					: await AttachmentAPI.getById(attachment.id);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = getAttachmentName(attachment);
			link.click();
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} catch {
			handleNotification?.(ERROR_OPERATION, "error");
		} finally {
			setDownloadingId(null);
		}
	};
	const viewAttachment = async (attachment) => {
		if (handleView) return handleView(attachment);
		const format = getPreviewFormat(attachment);
		if (!format) return downloadAttachment(attachment);
		const request = ++previewRequest.current;
		setPreview({attachment, kind: format[0], loading: true});
		try {
			const blob = await AttachmentAPI.getById(attachment.id);
			const text = format[0] === "text" ? await blob.text() : undefined;
			if (request !== previewRequest.current) return;
			const url =
				format[0] === "text"
					? undefined
					: URL.createObjectURL(new Blob([blob], {type: format[1]}));
			setPreview({attachment, kind: format[0], url, text, blob});
		} catch {
			if (request === previewRequest.current)
				setPreview({
					attachment,
					error:
						"Impossibile caricare l'allegato. Puoi riprovare o scaricarlo.",
				});
		}
	};
	const mediaError = () =>
		setPreview((current) => ({
			...current,
			error:
				"Il browser non riesce a visualizzare questo formato. Scarica l'allegato per aprirlo.",
		}));

	const uploadFile = async () => {
		setLoading(true);
		try {
			await AttachmentAPI.upload(
				entityType,
				entityId,
				selectedFile,
				selectedFile.name,
			);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			handleAdd();
			setOpenModal(false);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const renderAttachmentPreview = (attachment) => {
		const filePath = getRelativePath(attachment.filePath, pathUrl);

		if (isImageFile(attachment.filePath)) {
			if (authenticatedImages)
				return (
					<AuthenticatedAttachmentImage
						attachment={attachment}
						className={
							"attachment-img" +
							(attachmentClassName ? " " + attachmentClassName : "")
						}
					/>
				);
			return (
				<img
					className={
						"attachment-img" +
						(attachmentClassName ? " " + attachmentClassName : "")
					}
					src={filePath}
					alt=""
				/>
			);
		}
		const extension = getAttachmentExtension(attachment.filePath);
		if (extension === "pdf") return <PictureAsPdfOutlinedIcon />;
		if (isVideoFile(attachment.filePath)) return <MovieOutlinedIcon />;
		if (previewFormats[extension]?.[0] === "audio")
			return <AudioFileOutlinedIcon />;
		if (previewFormats[extension]?.[0] === "text")
			return <DescriptionOutlinedIcon />;
		return <InsertDriveFileOutlinedIcon />;
	};

	return (
		<>
			{editable && handleAdd && (
				<div className="d-flex align-items-center justify-content-end mb-2 mx-2">
					<Button
						variant="contained"
						onClick={() => {
							setOpenModal(true);
							setSelectedFile(null);
						}}
						size="small"
					>
						<div className="d-flex align-items-center">
							<AddIcon />
						</div>
					</Button>
				</div>
			)}
			<div className="attachments-container">
				{attachments?.length ? (
					attachments.filter(Boolean).map((attachment) => {
						const name = getAttachmentName(attachment);
						const format = getPreviewFormat(attachment);
						const canView = !!(handleView || format);
						return (
							<div className="attachment-item" key={attachment.id}>
								<div className="attachment-box">
									<button
										type="button"
										className={`attachment-preview attachment-preview--${format?.[0] || "file"}`}
										title={canView ? "Visualizza allegato" : "Scarica allegato"}
										aria-label={`${canView ? "Visualizza" : "Scarica anteprima"} ${name}`}
										onClick={() => viewAttachment(attachment)}
									>
										{renderAttachmentPreview(attachment)}
									</button>
									<div className="attachment-info">
										<div className="attachment-name" title={name}>
											{name}
										</div>
										<div className="attachment-footer">
											<span className="attachment-badge">
												{getAttachmentTypeLabel(attachment, name)}
											</span>
											<div className="attachment-actions">
												{editable &&
													handleEdit &&
													attachment.isTacticalBoard && (
														<Button
															className="attachment-action"
															variant="text"
															size="small"
															aria-label={`Modifica ${name}`}
															title="Modifica lavagna"
															onClick={() => handleEdit(attachment.id)}
														>
															<EditOutlinedIcon fontSize="small" />
														</Button>
													)}

												<Button
													className="attachment-action attachment-action--download"
													variant="text"
													size="small"
													aria-label={`Scarica ${name}`}
													title="Scarica allegato"
													disabled={downloadingId === attachment.id}
													onClick={() => downloadAttachment(attachment)}
												>
													<DownloadIcon fontSize="small" />
												</Button>
												{editable && handleDelete && (
													<Button
														className="attachment-action attachment-action--delete"
														variant="text"
														color="error"
														size="small"
														aria-label={`Elimina ${name}`}
														title="Elimina allegato"
														onClick={() => handleDelete(attachment.id)}
													>
														<DeleteIcon fontSize="small" />
													</Button>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					})
				) : (
					<div className="attachment-empty">
						<EmptyData />
					</div>
				)}
			</div>
			<VLFModal
				open={!!preview}
				title={preview ? getAttachmentName(preview.attachment) : "Allegato"}
				maxWidth="lg"
				paperClassName="attachment-viewer-dialog"
				close={closePreview}
				cancelLabel="Chiudi"
				confirm={
					preview ? () => downloadAttachment(preview.attachment) : undefined
				}
				confirmLabel="Scarica"
				disabled={downloadingId === preview?.attachment.id}
				content={
					<div
						className={`attachment-viewer${preview?.kind === "image" ? " attachment-viewer--image" : ""}`}
					>
						{preview?.loading ? (
							<CircularProgress aria-label="Caricamento allegato" />
						) : preview?.error ? (
							<p role="alert">{preview.error}</p>
						) : preview?.kind === "image" ? (
							<img
								src={preview.url}
								alt={getAttachmentName(preview.attachment)}
								onError={mediaError}
							/>
						) : preview?.kind === "pdf" ? (
							<iframe
								src={preview.url}
								title={getAttachmentName(preview.attachment)}
							/>
						) : preview?.kind === "video" ? (
							<video src={preview.url} controls onError={mediaError} />
						) : preview?.kind === "audio" ? (
							<audio src={preview.url} controls onError={mediaError} />
						) : preview?.kind === "text" ? (
							<pre>{preview.text}</pre>
						) : null}
					</div>
				}
			/>
			<VLFModal
				open={openModal}
				title="Aggiungi file multimediale"
				content={
					<div className="row mt-2 no-margin">
						<div className="col-sm-12 padding-small">
							{loading ? (
								<div className="d-flex align-items-center justify-content-center">
									<CircularProgress />
								</div>
							) : (
								<VLFUpload
									selectedFile={selectedFile}
									setSelectedFile={setSelectedFile}
								/>
							)}
						</div>
					</div>
				}
				confirm={uploadFile}
				close={() => setOpenModal(false)}
				confirmLabel="Conferma"
				disabled={loading}
			/>
		</>
	);
}
